import { BILLING_LIVE } from '@lib/constants/billing'
import { Banknote, Building2, CalendarDays, ChartColumn, Download, Funnel, Landmark, LayoutDashboard, List, Megaphone, MessageSquare, Palette, Settings, ShieldCheck, Sofa, Store, Users, Wallet, Waypoints, IdCard, Inbox, Star } from 'lucide-react'
import type { ReactNode } from 'react'
import { matchPath } from 'react-router-dom'
import { atCompanyLevel, can, isStaff, reachesCompany, worksAtAssignedVenues } from '@/auth/access'
import { isInTenantSupportMode, isInVenueSupportMode, isOrganizationAdmin, isSuperAdmin, isVendor } from '@/auth/roles'
import type { User } from '@/schemas/auth.schema'
import type { TenantFeature } from '@lib/enums/tenant-feature.enum'

export interface NavEntry {
  to: string
  label: string
  icon: ReactNode
  /**
   * Rendered as a plain anchor opening in a new tab. For destinations outside
   * the SPA — the design system is its own static document, so client-side
   * routing would try to resolve it as a route and fall through to the app.
   */
  external?: boolean
  /**
   * Pages outside its path that belong to it, as router patterns. A venue's
   * own Ads page is the Ads section's detail page, reached from its table, even
   * though it lives under `/venues`.
   */
  also?: string[]
}

/** How deep a path or pattern goes, in segments: the measure of how specific a match is. */
function depth(path: string): number {
  return path.split('/').filter(Boolean).length
}

/**
 * Which nav entry the current URL belongs to: the **deepest** one the URL sits
 * at or beneath, or null when none does.
 *
 * Nav paths nest. A venue's Settings (`/venues/1/settings`) lives under its
 * Dashboard (`/venues/1`), and a vendor's Venues and Furniture live under their
 * Settings (`/vendor`) — so a plain "is this a prefix" test lights two entries
 * at once. It did: on a venue's settings page both Dashboard and Settings showed
 * as selected.
 *
 * The other way round — marking the outer entries exact-match-only — is how this
 * was written, as a hardcoded list of two paths. The venue Dashboard was added
 * later and never joined the list, and a third nested entry would silently need
 * joining too. Longest match needs no list and cannot fall out of date.
 *
 * It also keeps a section lit on its own detail pages: `/events/123` is Events.
 * The exact-only rule left the nav with nothing selected there. A detail page
 * that lives elsewhere is named by its entry's `also`, and wins by being the
 * deeper match: `/venues/:venueId/ads` is Ads, not Venues.
 */
export function activeNavPath(pathname: string, entries: NavEntry[]): string | null {
  let best: { to: string; depth: number } | null = null
  for (const entry of entries) {
    // An external entry is another document, never the page you are on.
    if (entry.external === true) continue
    const matches = [
      ...(pathname === entry.to || pathname.startsWith(`${entry.to}/`) ? [depth(entry.to)] : []),
      ...(entry.also ?? []).filter((pattern) => matchPath({ path: pattern, end: false }, pathname) !== null).map(depth),
    ]
    if (matches.length === 0) continue
    const deepest = Math.max(...matches)
    if (best === null || deepest > best.depth) best = { to: entry.to, depth: deepest }
  }
  return best?.to ?? null
}

/**
 * Which sidebar entries a user sees: for a company's staff, the venue layout or
 * the company's by their role's reach, with only the entries their role allows
 * (docs/custom-roles.spec.md, "Navigation").
 *
 * A pure function, separate from the layout, because the *order* of these checks
 * carries meaning that is easy to break: the venue case has to be tested before
 * the company's, or someone who has entered a venue keeps the company's nav.
 * Rendering has nothing to do with that rule, and a rule worth a comment is
 * worth a test.
 *
 * `vendorOffersFurniture` is undefined until the vendor loads; the Furniture
 * entry shows meanwhile and is hidden only once we know it is not offered, so
 * the nav does not flicker on every page load.
 *
 * `features` are the company's switched-on features; an entry for one appears
 * only once the company is known to have it.
 */
export function navEntriesFor(
  user: User | null | undefined,
  { vendorOffersFurniture, features = [] }: { vendorOffersFurniture?: boolean; features?: readonly TenantFeature[] } = {},
): NavEntry[] {
  // Platform super admin (not in a tenant): tenants / vendors / super-admin users.
  // Support mode falls through to the tenant org-admin nav below.
  if (isSuperAdmin(user) && !isInTenantSupportMode(user)) {
    return [
      { to: '/tenants', label: 'Tenants', icon: <Landmark className="h-4 w-4" /> },
      { to: '/admin/vendors', label: 'Vendors', icon: <Store className="h-4 w-4" /> },
      // Reviews wait here until one of them approves (docs/vendor-growth.spec.md, "Reviews").
      { to: '/admin/reviews', label: 'Reviews', icon: <Star className="h-4 w-4" /> },
      { to: '/super-admins', label: 'Users', icon: <Users className="h-4 w-4" /> },
      {
        to: '/design-system/gallery.html',
        label: 'Design system',
        icon: <Palette className="h-4 w-4" />,
        external: true,
      },
    ]
  }
  // A vendor user manages their vendor; Furniture appears only while offered.
  if (isVendor(user)) {
    const entries: NavEntry[] = [
      // The events it is on come first: for a planner they are the work.
      { to: '/vendor/events', label: 'Events', icon: <CalendarDays className="h-4 w-4" /> },
      // Quote requests, which are leads (docs/vendor-growth.spec.md, "Quote requests").
      { to: '/vendor/requests', label: 'Requests', icon: <Inbox className="h-4 w-4" /> },
      // What couples and planners said, once approved (docs/vendor-growth.spec.md, "Reviews").
      { to: '/vendor/reviews', label: 'Reviews', icon: <Star className="h-4 w-4" /> },
      // What venues and planners see of it (docs/vendor-growth.spec.md).
      { to: '/vendor/profile', label: 'Profile', icon: <IdCard className="h-4 w-4" /> },
      { to: '/vendor', label: 'Settings', icon: <Settings className="h-4 w-4" /> },
      { to: '/vendor/venues', label: 'Venues', icon: <Building2 className="h-4 w-4" /> },
    ]
    if (vendorOffersFurniture !== false) {
      entries.push({ to: '/vendor/furniture', label: 'Furniture', icon: <Sofa className="h-4 w-4" /> })
    }
    // Everyone at the company sees its people; only owners change them.
    entries.push({ to: '/vendor/people', label: 'People', icon: <Users className="h-4 w-4" /> })
    return entries
  }
  const events: NavEntry = { to: '/events', label: 'Events', icon: <CalendarDays className="h-4 w-4" /> }
  // A client's events are all they have.
  if (!isStaff(user)) return [events]
  const feedback: NavEntry = { to: '/feedback', label: 'Feedback', icon: <MessageSquare className="h-4 w-4" /> }
  const seesEvents = can(user, 'events:view')
  // Inside a venue the nav is the venue's, whoever you are. Someone on a
  // Company-wide role who has entered one is working as that venue's staff, so
  // leaving the company's entries in place would offer to manage things the
  // session is not currently pointed at — and a Venues list would just be the
  // venue they are already in. The picker above the nav is the way back out.
  if (worksAtAssignedVenues(user) || isInVenueSupportMode(user)) {
    // The venue is three pages now: its dashboard (how it is doing and what
    // needs doing), its calendar (what is on) and its settings (spaces,
    // inventory, vendors and how it runs). All need a venue to point at, so
    // none appears until one is selected.
    const venueId = isInVenueSupportMode(user) ? user.supportVenue.venueId : null
    if (venueId === null) {
      if (!seesEvents) return []
      return can(user, 'events:edit') ? [events, feedback] : [events]
    }
    return [
      // The venue's own page leads: it answers "how is the venue doing, and
      // what needs doing", the questions someone opens the app with.
      { to: `/venues/${venueId}`, label: 'Dashboard', icon: <LayoutDashboard className="h-4 w-4" /> },
      // How couples move from inquiry to the event, with or without GoHighLevel.
      ...(can(user, 'leadStats:view')
        ? [{ to: `/venues/${venueId}/pipeline`, label: 'Pipeline', icon: <Funnel className="h-4 w-4" /> }]
        : []),
      // What is on, as a shape in time; Events is the list behind it, so it
      // takes a list's icon here, where the two sit side by side.
      ...(seesEvents
        ? [
            { to: `/venues/${venueId}/calendar`, label: 'Calendar', icon: <CalendarDays className="h-4 w-4" /> },
            { ...events, icon: <List className="h-4 w-4" /> },
          ]
        : []),
      ...(can(user, 'events:edit') ? [feedback] : []),
      // Furniture is a section of Settings rather than a nav entry of its
      // own: the catalog says what a piece is, the inventory beneath it
      // says how many the venue has, and the two are read together.
      ...(seesVenueSettings(user)
        ? [{ to: `/venues/${venueId}/settings`, label: 'Settings', icon: <Settings className="h-4 w-4" /> }]
        : []),
    ]
  }
  // Across the company, staff navigate by venue, so it leads.
  const entries: NavEntry[] = [{ to: '/venues/all', label: 'Venues', icon: <Building2 className="h-4 w-4" />, also: ['/venues/:venueId'] }]
  if (seesEvents) entries.push(events)
  if (can(user, 'events:edit')) entries.push(feedback)
  // Furniture is deliberately absent: the company owns none, so an entry here
  // could only lead somewhere that asks which venue you meant.
  if (can(user, 'people:view')) entries.push({ to: '/users', label: 'Users', icon: <Users className="h-4 w-4" /> })
  // What each kind of staff member can do: an organization admin's alone.
  if (isOrganizationAdmin(user)) entries.push({ to: '/roles', label: 'Roles', icon: <ShieldCheck className="h-4 w-4" /> })
  // Every venue's lead stats and the pipeline beside them, then the GoHighLevel
  // settings behind them; each venue's connections are on its own page. The
  // pipeline needs no GoHighLevel: a venue's events record its core stages.
  const hasGhl = features.includes('gohighlevel')
  if (hasGhl && can(user, 'leadStats:view')) entries.push({ to: '/leads', label: 'Leads', icon: <ChartColumn className="h-4 w-4" /> })
  // A venue's pipeline, opened from the company's, is still the Pipeline section.
  if (can(user, 'leadStats:view')) entries.push({ to: '/pipeline', label: 'Pipeline', icon: <Funnel className="h-4 w-4" />, also: ['/venues/:venueId/pipeline'] })
  if (hasGhl && can(user, 'integrations:edit') && reachesCompany(user)) {
    entries.push({ to: '/gohighlevel', label: 'GoHighLevel', icon: <Waypoints className="h-4 w-4" /> })
  }
  // The business that takes the company's payments (docs/sales.spec.md): every company has Sales.
  if (can(user, 'integrations:edit') && reachesCompany(user)) {
    entries.push({ to: '/sales/business', label: 'Getting paid', icon: <Banknote className="h-4 w-4" /> })
  }
  // Every venue's ad numbers, and for those who set them up, the Google sign-ins that bring them in.
  if (features.includes('ads') && can(user, 'ads:view')) {
    entries.push({ to: '/ads', label: 'Ads', icon: <Megaphone className="h-4 w-4" />, also: ['/venues/:venueId/ads'] })
  }
  // Every organization admin can see where billing stands; only the billing
  // owner can act on it, and the page says so. Hidden until billing is live.
  if (BILLING_LIVE && isOrganizationAdmin(user)) entries.push({ to: '/billing', label: 'Billing', icon: <Wallet className="h-4 w-4" /> })
  // Temporary migration console — remove after Social Tables cutover.
  if (can(user, 'imports:edit') && reachesCompany(user)) {
    entries.push({ to: '/admin/prism-import', label: 'Prism import', icon: <Download className="h-4 w-4" /> })
  }
  // Super admins in support mode get the organization admin's nav only — they
  // leave via the tenant banner, not a Platform shortcut in the tree.
  return entries
}

/** The venue settings page's areas that are worth opening it to look at. People is its Staff section. */
const VENUE_SETTINGS_AREAS = ['spaces', 'furniture', 'venueSetup', 'vendors', 'marketing', 'sales', 'people'] as const

/**
 * Whether the venue's Settings page has anything this session's role can see.
 * Lead stats count only with Edit: the numbers are on the dashboard, and what
 * they are made of is there to be changed. GoHighLevel's connections count for
 * whoever sets up integrations, and the company's own decisions about a venue
 * (its name and address, whether it stays) for whoever makes them, from across
 * the company.
 */
export function seesVenueSettings(user: User | null | undefined): boolean {
  if (VENUE_SETTINGS_AREAS.some((area) => can(user, `${area}:view`))) return true
  if (can(user, 'leadStats:edit') || can(user, 'integrations:edit')) return true
  return atCompanyLevel(user) && (can(user, 'venues:edit') || can(user, 'deleteEvents'))
}
